import React, { useState, useEffect } from "react";
import axios from "axios";
import { decode } from "marvel-snap-deckstrings";

function AddSnapDeck({ onClose, setDecks, user, dividors }) {
  const [form, setForm] = useState({
    deckName: "",
    versionNum: "",
    monthCompleted: "",
    deckRank: "",
    deckRanking: "",
    cards: Array(12).fill(""),
  });

  const [allCards, setAllCards] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // 🔹 Fetch all cards once
  useEffect(() => {
    const fetchCards = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}api/data/snapAllCards`
        );
        setAllCards(res.data);
      } catch (err) {
        console.error("Failed to fetch cards:", err);
      }
    };
    fetchCards();
  }, []);

  const handleChange = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleCardChange = (index, cardId) => {
    setForm((prev) => {
      const updated = [...prev.cards];
      updated[index] = cardId;
      return { ...prev, cards: updated };
    });
  };

  // same helpers you had in MyDecks
  function camelToWords(str) {
    if (!str) return "";
    return str
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
      .trim();
  }

  function normalizeToDbFormat(str) {
    return camelToWords(str)
      .split(" ")
      .map((w, i) =>
        i === 0
          ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
          : w.toLowerCase()
      )
      .join(" ");
  }

  const handleSubmit = async () => {
    try {
      const payload = {
        userId: user.id,
        deckName: form.deckName,
        versionNum: form.versionNum,
        monthCompleted: form.monthCompleted,
        deckRank: form.deckRank || "Neutral",
        deckRanking: form.deckRanking ? parseFloat(form.deckRanking) : null,
        ...Object.fromEntries(form.cards.map((c, i) => [`card${i + 1}`, c || null])),
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}api/addSnapDeck`,
        payload
      );

      // Add new deck to local state
      setDecks((prev) => [...prev, { id: res.data.id, ...payload }]);
      onClose();
    } catch (err) {
      console.error("Failed to add deck:", err);
      alert("Could not add deck. Check console for details.");
    }
  };

  function toPascalCase(str) {
    return str
      .replace(/[aeiouy]/g, "") 
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join("")
      .replace(".", "")
      .replace("-", "")
      .replace("HwkKtBshp", "KtBshp")
      .replace("RnnThCcsr", "Rnn")
      .replace("ThHd", "Hd");
  }

  const [allDecodeCards, setAllDecodeCards] = useState({});

  async function testDecode(rawCode) {
  // 1. Base64 decode
  const decoded = atob(rawCode);

  let dict = allDecodeCards; // fallback to existing state

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}api/data/snapAllCards`
    );

    // build dictionary { PascalCaseName: cardId }
    dict = {};
    res.data.forEach(c => {
      const key = toPascalCase(c.cardname);
      if (!dict[key]) {
        dict[key] = c.id;
      }
    });

    setAllDecodeCards(dict); // update state for later
    console.log("Card dictionary:", dict);
  } catch (err) {
    console.error("Error fetching cards:", err);
  }

  // 2. Split by commas
  const parts = decoded.split(",").map(w => w.slice(0, w.length - 1));
  console.log("Decoded parts:", parts);

  // 3. Use the local dict immediately (not stale state)
  const cardIds = parts.map(p => dict[p] || null);

  // 4. Lookup card objects, sort by cost then power
  const sortedCards = cardIds
    .map(id => allCards.find(c => c.id === id))
    .filter(Boolean)
    .sort((a, b) => {
      if (a.cardcost !== b.cardcost) return a.cardcost - b.cardcost;
      return a.cardpower - b.cardpower;
    });

  const sortedIds = sortedCards.map(c => c.id);
  while (sortedIds.length < 12) sortedIds.push(null);

  console.log("Sorted card IDs:", sortedIds);
  return sortedIds;
}

  function extractDeckCode(text) {
    // Match any base64-looking string on its own line
    const match = text.match(/^[A-Za-z0-9+/=]+$/m);
    return match ? match[0] : null;
  }

  const handleImportDeckCode = async () => {
    try {
      const rawClipboard = await navigator.clipboard.readText();
      const rawCode = extractDeckCode(rawClipboard);

      if (!rawCode) {
        alert("No valid deck code found in clipboard.");
        return;
      }

      const cardIds = await testDecode(rawCode);

      if (cardIds && cardIds.length === 12) {
        setForm(prev => ({ ...prev, cards: cardIds }));
      } else {
        alert("Import failed: not enough cards found.");
      }
    } catch (err) {
      console.error("Failed to read clipboard:", err);
      alert("Could not read from clipboard. Please copy a deck code first.");
    }
  };

  const [gridCols, setGridCols] = useState(6);
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w <= 620) return 3;
      if (w <= 980) return 4;
      return 6; // desktop
    };

    const onResize = () => setGridCols(calc());
    onResize();

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 🔹 Filter cards by search term
  const filteredCards = allCards.filter((c) =>
    c.cardname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(920px, 100%)",
          padding: "16px",
        }}
      >
        <h2 style={{ textAlign: "center", margin: "6px 0 14px" }}>Add Deck</h2>

        {/* Deck Info */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "10px",
            margin: "0 0 12px",
          }}
        >
          <input
            type="text"
            placeholder="Deck Name"
            value={form.deckName}
            onChange={(e) => handleChange("deckName", e.target.value)}
          />
          <input
            type="text"
            placeholder="Version Number"
            value={form.versionNum}
            onChange={(e) => handleChange("versionNum", e.target.value)}
          />
          <input
            type="text"
            placeholder="Month Completed"
            value={form.monthCompleted}
            onChange={(e) => handleChange("monthCompleted", e.target.value)}
          />

          <select
            value={form.deckRank}
            onChange={(e) => handleChange("deckRank", e.target.value)}
          >
            <option value="">Select Dividor</option>
            {(dividors || []).map((d) => (
              <option key={d.id} value={d.dividor}>
                {d.dividor}
              </option>
            ))}
          </select>
        </div>

        {/* 📋 Import Deck Code */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "10px",
            marginBottom: "12px",
            justifyItems: "center",
          }}
        >
          <button
            className="button"
            onClick={handleImportDeckCode}
            style={{
              width: "min(520px, 100%)",
              padding: "12px 14px",
            }}
          >
            Import Deck Code
          </button>
        </div>

        {/* Current Deck Cards */}
        <h3 style={{ margin: "10px 0 10px" }}>Current Cards</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
            gap: "10px",
          }}
        >
          {form.cards.map((cardId, i) => {
            const card = allCards.find((c) => c.id === cardId);
            return (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  border: "1px solid #444",
                  borderRadius: "10px",
                  padding: "6px",
                  background: "#111",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                {card ? (
                  <img
                    src={card.cardhtml}
                    alt={card.cardname}
                    style={{
                      width: "100%",
                      borderRadius: "10px",
                      aspectRatio: "3 / 4",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      color: "#666",
                      borderRadius: "10px",
                      border: "1px dashed #333",
                      padding: "18px 8px",
                      minHeight: "110px",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    Empty
                  </div>
                )}

                <button
                  className="button"
                  onClick={() => handleCardChange(i, "")}
                  style={{
                    padding: "10px 10px",
                    width: "100%",
                  }}
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>

        {/* Search & Add Cards */}
        <h3 style={{ marginTop: "16px" }}>Search Cards</h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "10px",
            marginBottom: "10px",
          }}
        >
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>

        <div
          style={{
            border: "1px solid #222",
            borderRadius: "12px",
            padding: "10px",
            background: "#0f0f0f",
            maxHeight: "320px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
              gap: "10px",
            }}
          >
            {filteredCards.map((c) => (
              <div
                key={c.id}
                style={{
                  cursor: "pointer",
                  textAlign: "center",
                  background: "#1a1a1a",
                  padding: "6px",
                  borderRadius: "10px",
                  border: "1px solid rgba(197,176,55,0.25)",
                }}
                onClick={() => {
                  const emptyIndex = form.cards.findIndex((id) => !id);
                  if (emptyIndex !== -1) {
                    handleCardChange(emptyIndex, c.id);
                  } else {
                    alert("Deck is full. Remove a card first.");
                  }
                }}
              >
                <img
                  src={c.cardhtml}
                  alt={c.cardname}
                  style={{
                    width: "100%",
                    borderRadius: "10px",
                    aspectRatio: "3 / 4",
                    objectFit: "cover",
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "10px",
            marginTop: "16px",
          }}
        >
          <button
            className="button"
            onClick={handleSubmit}
            style={{ padding: "12px 14px", width: "100%" }}
          >
            Save
          </button>
          <button
            className="button"
            onClick={onClose}
            style={{ padding: "12px 14px", width: "100%" }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddSnapDeck;
